import { supabase } from './supabase';
import type { BroadcastDraft } from './broadcast';

/**
 * Envoi des messages groupés (parité legacy). Persiste la campagne dans
 * `broadcast_campaigns` puis déclenche l'Edge Function `send-broadcast` (email/push).
 * RÉSILIENT : si la table / la fonction n'existent pas encore, on ne casse pas l'UI.
 */

export interface BroadcastHistoryRow {
  id: string;
  type: string;
  emoji: string;
  title: string;
  audience: string;
  channels: string[];
  sent_count: number;
  status: string;
  created_at: string;
}

export async function loadBroadcastHistory(): Promise<BroadcastHistoryRow[]> {
  const { data, error } = await supabase
    .from('broadcast_campaigns')
    .select('id, type, emoji, title, audience, channels, sent_count, status, created_at')
    .order('created_at', { ascending: false })
    .limit(20);
  if (error || !Array.isArray(data)) {
    if (error) console.warn('[broadcast] historique indisponible:', error.message);
    return [];
  }
  return data as unknown as BroadcastHistoryRow[];
}

export interface SendResult {
  ok: boolean;
  message: string;
}

/**
 * Crée la campagne + déclenche l'envoi. Si l'Edge Function n'est pas déployée,
 * la campagne est enregistrée en statut « draft » et on le signale clairement.
 */
export async function sendBroadcast(draft: BroadcastDraft): Promise<SendResult> {
  // 1) Persiste la campagne (statut queued).
  const insert = await supabase
    .from('broadcast_campaigns')
    .insert({
      type: draft.type,
      emoji: draft.emoji,
      title: draft.title.trim(),
      body: draft.body.trim(),
      url: draft.url?.trim() || null,
      audience: draft.audience,
      channels: draft.channels,
      status: 'queued',
      sent_count: 0,
    })
    .select('id')
    .single();

  if (insert.error) {
    return { ok: false, message: `Campagne non enregistrée (migration manquante ?) : ${insert.error.message}` };
  }
  const campaignId = (insert.data as { id: string }).id;

  // 2) Déclenche l'envoi réel (email/push) via Edge Function.
  try {
    const { data, error } = await supabase.functions.invoke('send-broadcast', {
      body: { campaign_id: campaignId },
    });
    if (error) {
      return { ok: true, message: '📥 Campagne enregistrée. Envoi en attente (Edge Function `send-broadcast` à déployer).' };
    }
    const sent = (data as { sent?: number } | null)?.sent ?? 0;
    return { ok: true, message: `✅ Envoyé à ${sent} destinataire(s).` };
  } catch {
    return { ok: true, message: '📥 Campagne enregistrée. Envoi en attente (Edge Function `send-broadcast` à déployer).' };
  }
}
